import { Injectable } from "@angular/core";
import { UntypedFormGroup, Validators } from "@angular/forms";

import { DffFormControlType, DffOprindeligAktuelVaerdiFormGroup, DffReactiveFormControlValueMapper as Map } from "@dffedb/forms";
import { DffOprindeligAktuelVaerdi } from "@dffedb/util";
import { Huskeliste } from "@e-forsyning/common/huskeliste";
import { Maaler, MaalerOprettelseFormBuilder, MaalerType } from "@e-forsyning/common/maaler";
import { NoteBruger, NoteHenvendelsestype, NoteOmraade, NoteOpgavestyringStatus } from "@e-forsyning/common/note";

import { InstallationsoprettelseData } from "./model/installationsoprettelse-data.model";
import { Installationsoprettelse } from "./model/installationsoprettelse.model";

@Injectable({ providedIn: "root" })
export class InstallationsoprettelseFormBuilder {
    public installationsoprettelseDataToFormGroup(
        installationsoprettelseData: InstallationsoprettelseData,
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsetyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const result = new UntypedFormGroup({
            installationsoprettelse: this.installationsoprettelseToFormGroup(
                installationsoprettelseData.installationsoprettelse,
                maalerePaaLager,
                maalerTyper,
                huskelister,
                noteOmraader,
                noteHenvendelsetyper,
                noteOpgavestyringStatusser,
                noteBrugere
            )
        });
        return result;
    }

    public installationsoprettelseToFormGroup(
        installationsoprettelse: Installationsoprettelse,
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        huskelister: Huskeliste[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const nyMaaler = MaalerOprettelseFormBuilder.maalerAflaesningMedNoterToFormGroup(
            installationsoprettelse.nyMaaler,
            maalerePaaLager,
            maalerTyper,
            noteOmraader,
            noteHenvendelsestyper,
            noteOpgavestyringStatusser,
            noteBrugere
        );
        const result = new UntypedFormGroup({
            dato: new DffOprindeligAktuelVaerdiFormGroup(
                installationsoprettelse.dato,
                { controlType: DffFormControlType.DateInput, placeholder: "Dato for installationsoprettelse" },
                Validators.required
            ),
            huskelisteId: this.huskelisteIdToFormControl(installationsoprettelse.huskelisteId, huskelister),
            info1: new DffOprindeligAktuelVaerdiFormGroup(
                installationsoprettelse.info1,
                { placeholder: "Info 1", controlType: DffFormControlType.TextInput },
                [Validators.maxLength(30)]
            ),
            info2: new DffOprindeligAktuelVaerdiFormGroup(
                installationsoprettelse.info2,
                { placeholder: "Info 2", controlType: DffFormControlType.TextInput },
                [Validators.maxLength(30)]
            ),
            nyMaaler
        });
        return result;
    }

    public huskelisteIdToFormControl(huskelisteId: DffOprindeligAktuelVaerdi<number>, huskelister: Huskeliste[]): UntypedFormGroup {
        const result = new DffOprindeligAktuelVaerdiFormGroup(huskelisteId, {
            controlType: DffFormControlType.DropdownList,
            dropdownListOptions: [{}, ...huskelister],
            placeholder: "Huskeliste"
        });
        return result;
    }

    public formGroupToInstallationsoprettelseData(formGroup: UntypedFormGroup): InstallationsoprettelseData {
        const result: InstallationsoprettelseData = {
            installationsoprettelse: this.formGroupToInstallationsoprettelse(formGroup.get("installationsoprettelse") as UntypedFormGroup)
        };
        return result;
    }

    public formGroupToInstallationsoprettelse(formGroup: UntypedFormGroup): Installationsoprettelse {
        const nyMaaler = MaalerOprettelseFormBuilder.formGroupToMaalerAflaesningMedNoter(formGroup.get("nyMaaler") as UntypedFormGroup);
        const result: Installationsoprettelse = {
            nyMaaler,
            dato: Map.toOprindeligAktuelDates(formGroup.controls["dato"]),
            huskelisteId: Map.toOprindeligAktuelIntegers(formGroup.controls["huskelisteId"]),
            info1: Map.toOprindeligAktuelStrings(formGroup.controls["info1"]),
            info2: Map.toOprindeligAktuelStrings(formGroup.controls["info2"])
        };
        return result;
    }
}
